import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { UsageLog, ClientDashboardStats } from '../../types';

const ClientUsage: React.FC = () => {
  const [usageLogs, setUsageLogs] = useState<UsageLog[]>([]);
  const [stats, setStats] = useState<ClientDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'success' | 'failed'>('all');
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month'>('week');

  useEffect(() => {
    loadUsage();
  }, [dateRange]);

  const loadUsage = async () => {
    setLoading(true);
    const response = await api.getClientDashboard();
    if (response.success && response.data) {
      setStats(response.data);
      setUsageLogs(response.data.recentUsage || []);
    }
    setLoading(false);
  };

  const filteredLogs = usageLogs.filter(log => {
    if (filter === 'success') return log.success;
    if (filter === 'failed') return !log.success;
    return true;
  });

  const totalRequests = usageLogs.length;
  const successfulRequests = usageLogs.filter(l => l.success).length;
  const failedRequests = usageLogs.filter(l => !l.success).length;
  const totalTokens = usageLogs.reduce((sum, l) => sum + l.requestTokens + l.responseTokens, 0);
  const avgProcessingTime = usageLogs.length > 0
    ? Math.round(usageLogs.reduce((sum, l) => sum + l.processingTimeMs, 0) / usageLogs.length)
    : 0;

  // Group by feature
  const featureBreakdown = usageLogs.reduce((acc, log) => {
    acc[log.featureCode] = (acc[log.featureCode] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
          <p className="text-neutral-500 text-sm">Loading usage history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Usage History</h1>
          <p className="text-neutral-500">Track your GEO-PRO engine usage</p>
        </div>
        <div className="flex gap-2">
          {(['today', 'week', 'month'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                dateRange === range
                  ? 'bg-emerald-600 text-white'
                  : 'bg-[#1a1a1a] border border-[#262626] text-neutral-400 hover:bg-[#262626] hover:text-white'
              }`}
            >
              {range.charAt(0).toUpperCase() + range.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Current Usage */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">📊</span>
                <div>
                  <p className="text-neutral-400 text-sm">Today's Usage</p>
                  <p className="text-2xl font-bold text-white">
                    {stats.usageToday} <span className="text-neutral-500 text-lg">/ {stats.dailyLimit}</span>
                  </p>
                </div>
              </div>
            </div>
            <div className="w-full bg-[#262626] rounded-full h-2">
              <div
                className="h-2 rounded-full bg-emerald-500 transition-all"
                style={{ width: `${Math.min((stats.usageToday / stats.dailyLimit) * 100, 100)}%` }}
              />
            </div>
          </div>

          <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">📈</span>
                <div>
                  <p className="text-neutral-400 text-sm">This Month</p>
                  <p className="text-2xl font-bold text-white">
                    {stats.usageThisMonth} <span className="text-neutral-500 text-lg">/ {stats.monthlyLimit}</span>
                  </p>
                </div>
              </div>
            </div>
            <div className="w-full bg-[#262626] rounded-full h-2">
              <div
                className="h-2 rounded-full bg-blue-500 transition-all"
                style={{ width: `${Math.min((stats.usageThisMonth / stats.monthlyLimit) * 100, 100)}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-[#121212] border border-[#262626] rounded-xl p-4">
          <p className="text-neutral-500 text-xs uppercase">Total Requests</p>
          <p className="text-2xl font-bold text-white">{totalRequests}</p>
        </div>
        <div className="bg-[#121212] border border-[#262626] rounded-xl p-4">
          <p className="text-neutral-500 text-xs uppercase">Successful</p>
          <p className="text-2xl font-bold text-emerald-400">{successfulRequests}</p>
        </div>
        <div className="bg-[#121212] border border-[#262626] rounded-xl p-4">
          <p className="text-neutral-500 text-xs uppercase">Failed</p>
          <p className="text-2xl font-bold text-red-400">{failedRequests}</p>
        </div>
        <div className="bg-[#121212] border border-[#262626] rounded-xl p-4">
          <p className="text-neutral-500 text-xs uppercase">Total Tokens</p>
          <p className="text-2xl font-bold text-blue-400">{totalTokens.toLocaleString()}</p>
        </div>
        <div className="bg-[#121212] border border-[#262626] rounded-xl p-4">
          <p className="text-neutral-500 text-xs uppercase">Avg Time</p>
          <p className="text-2xl font-bold text-yellow-400">{avgProcessingTime}ms</p>
        </div>
      </div>

      {/* Feature Breakdown */}
      {Object.keys(featureBreakdown).length > 0 && (
        <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#262626]">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Usage by Feature</h3>
          </div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(featureBreakdown)
              .sort((a, b) => b[1] - a[1])
              .map(([feature, count]) => (
                <div key={feature} className="bg-[#1a1a1a] rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-white font-medium text-sm">{feature}</p>
                    <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded">
                      {count}
                    </span>
                  </div>
                  <div className="mt-2 w-full bg-[#262626] rounded-full h-1">
                    <div
                      className="h-1 rounded-full bg-emerald-500"
                      style={{ width: `${(count / totalRequests) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Usage Logs Table */}
      <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-[#262626] flex items-center justify-between">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Request History</h3>
          <div className="flex gap-2">
            {(['all', 'success', 'failed'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                  filter === f
                    ? f === 'success'
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : f === 'failed'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-[#262626] text-white'
                    : 'text-neutral-500 hover:text-white'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {filteredLogs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1a1a1a]">
                <tr>
                  <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Feature</th>
                  <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Status</th>
                  <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Tokens</th>
                  <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Time</th>
                  <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#262626]">
                {filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-[#1a1a1a] transition-colors">
                    <td className="p-4">
                      <p className="text-white font-medium">{log.featureCode}</p>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                        log.success
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {log.success ? 'Success' : 'Failed'}
                      </span>
                    </td>
                    <td className="p-4">
                      <p className="text-neutral-400 text-sm">
                        {log.requestTokens + log.responseTokens > 0
                          ? (log.requestTokens + log.responseTokens).toLocaleString()
                          : '-'}
                      </p>
                    </td>
                    <td className="p-4">
                      <p className="text-neutral-400 text-sm">{log.processingTimeMs}ms</p>
                    </td>
                    <td className="p-4">
                      <p className="text-neutral-400 text-sm">
                        {new Date(log.createdAt).toLocaleString('en-BD', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-[#1a1a1a] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">📭</span>
            </div>
            <p className="text-neutral-500">No usage history found</p>
            <p className="text-neutral-600 text-sm mt-1">Start using the GEO-PRO engine to see your history here</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientUsage;
